import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { AppBar, Avatar, Button, Divider, IconButton, InputAdornment, Link, Menu, MenuItem, OutlinedInput, Stack, Toolbar, Typography, useTheme } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from "react";
import { DialogsContext } from '../contexts';
import { useAppStore } from './store';
import { useMenu } from '../common/dialog.util';
import authService from '../auth/auth.service';
import UserAvatar from '../user/user.avatar';
import Can from '../auth/can';

export default function MenuTop(props) {
	const { user } = useAppStore();
	const links = [
		{ label: 'Catchup', link: '/catchup', permission: 'catchup.view' },
		{ label: 'Upcoming', link: '/upcoming', permission: 'upcoming.view' },
		{ label: 'Daily', link: '/calendar/daily', permission: 'daily.view' },
		{ label: '3-Day', link: '/calendar/3-day', permission: 'threeDay.view' },
		{ label: 'Weekly', link: '/calendar/weekly', permission: 'weekly.view' },
		{ label: 'Monthly', link: '/calendar/monthly', permission: 'monthly.view' },
	];

	const menu = useMenu();
	const { dialogs } = useContext(DialogsContext);
	const toolbarRef = useRef(null);
	const linksRef = useRef([]);
	const [linkWidths, setLinkWidths] = useState(new Array(links.length).fill(0));
	const [visibleLinks, setVisibleLinks] = useState(links);

	const theme = useTheme();

	const calculateVisibleLinks = () => {
		if (!toolbarRef.current) return;

		const toolbarWidth = toolbarRef.current.clientWidth;
		let totalWidth = 0;
		const newVisibleLinks = [];

		for (let i = 0; i < links.length; i++) {
			let linkWidth = linksRef.current[i]?.getBoundingClientRect().width || 0;
			linkWidth = linkWidth ? linkWidth : linkWidths[i];
			linkWidths[i] = linkWidth;
			totalWidth += linkWidth;

			if (totalWidth < (toolbarWidth - 450)) {
				newVisibleLinks.push(links[i]);
			}
		}

		setVisibleLinks(newVisibleLinks);
		setLinkWidths(linkWidths);
	};

	useEffect(() => {
		const pageTitles = [...links];
		pageTitles.push({ label: 'Students', link: '/students', permission: 'student.list' });
		pageTitles.push({ label: 'Subjects', link: '/subjects', permission: 'subject.list' });
		pageTitles.push({ label: 'Courses', link: '/courses', permission: 'course.list' });
		pageTitles.push({ label: 'Todos', link: '/todos', permission: 'todo.list' });
		pageTitles.push({ label: 'Users', link: '/users', permission: 'user.list' });
		pageTitles.push({ label: 'Enrollments', link: '/enrollments', permission: 'monthly.list' });

		const match = _.find(pageTitles, (entry) => window.location?.pathname?.startsWith(entry.link));
		if (match) {
			document.title = match.label + ' | Prospect Academy';
		}
		else {
			document.title = 'Prospect Academy';
		}
	}, []);

	useEffect(() => {
		const throttled = _.throttle(calculateVisibleLinks, 200);
		calculateVisibleLinks();

		// Run on mount and when the window resizes
		const observer = new ResizeObserver(throttled);
		observer.observe(toolbarRef.current);
		window.addEventListener("resize", throttled);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", throttled);
		};
	}, []);

	const toggle = () => {
		props.setOpen?.(!props.open)
	}

	const logout = async () => {
		await authService.logout();
		window.location.pathname = "/";
	}

	return (
		<AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
			<Toolbar ref={toolbarRef}>
				<Stack alignItems="center" sx={{ flexGrow: 1 }}>
					<IconButton sx={{ color: "inherit" }} onClick={toggle}>
						<MenuIcon />
					</IconButton>
					<Link href="/" sx={{ color: "inherit" }} underline="none">
						<Typography variant="h1" noWrap>Prospect Academy</Typography>
					</Link>
					<Divider orientation="vertical" variant="middle" flexItem sx={{ pl: 1.5 }} />
					{visibleLinks.map((link, index) => (
						<Can key={link.label} permission={link.permission}>
							<Button
								href={link.link}
								ref={(el) => (linksRef.current[index] = el)}
								sx={[
									theme.mixins.toolbar,
									{ borderRadius: 0, color: "inherit" },
									{ display: visibleLinks.includes(link) ? "inline-flex" : "none" },
									window.location.pathname.indexOf(link.link) == 0 ? { backgroundColor: theme.palette.primary.light } : {}
								]}>
								{link.label}
							</Button>
						</Can>
					))}
				</Stack>
				<Stack alignItems="center" justifyContent="flex-end">
					<IconButton sx={{ color: "white" }} onClick={() => dialogs.open('search')}>
						<SearchIcon />
					</IconButton>
					<IconButton onClick={(e) => menu.open(e)}>
						<UserAvatar user={user} size={32} />
					</IconButton>
					<Menu open={menu.isOpen} onClose={() => menu.close()} anchorEl={menu.anchor} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} disableScrollLock={true}>
						<MenuItem><Link href="/profile">Profile</Link></MenuItem>
						<Divider />
						<MenuItem onClick={() => { menu.close(); logout() }}>Logout</MenuItem>
					</Menu>
				</Stack>
			</Toolbar>
		</AppBar>
	);
}